import React from 'react';
import { Advisory, AdvisorySeverity } from '../services/advisoryRules';

interface AgentAdvisoriesProps {
  advisories: Advisory[];
  mode: 'INLINE' | 'PANEL' | 'TOAST';
  moduleLabel: string;
  onAcknowledge?: () => void;
  showEmptyState?: boolean;
}

const severityStyles: Record<AdvisorySeverity, string> = {
  LOW: 'bg-slate-50 text-slate-600 border-slate-100',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  HIGH: 'bg-rose-50 text-rose-700 border-rose-200',
};

const AgentAdvisories: React.FC<AgentAdvisoriesProps> = ({ advisories, mode, moduleLabel, onAcknowledge, showEmptyState }) => {
  if (!advisories.length && !showEmptyState) return null;

  const content = (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-600">Supervisor Agent</p>
          <p className="text-base font-black text-slate-900 break-words">{moduleLabel} Advisory Feed</p>
        </div>
        {onAcknowledge && (
          <button
            onClick={onAcknowledge}
            className="px-4 py-2 text-[10px] font-black uppercase tracking-[0.3em] text-white bg-slate-900 rounded-xl shadow-sm hover:bg-slate-800 transition-all"
          >
            Acknowledge All
          </button>
        )}
      </div>

      <div className="space-y-3">
        {advisories.length === 0 && (
          <div className="border rounded-2xl p-4 bg-emerald-50 text-emerald-700 border-emerald-200">
            <div className="flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-[0.3em]">No Issues Detected</p>
              <span className="text-[10px] font-black uppercase tracking-[0.3em]">OK</span>
            </div>
            <p className="text-sm font-semibold mt-2 text-emerald-700">Current inputs pass all active rules.</p>
          </div>
        )}
        {advisories.map(item => (
          <div key={item.id} className={`border rounded-2xl p-4 ${severityStyles[item.severity]}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-[0.2em] break-words">{item.title}</p>
              <span className="text-[10px] font-black uppercase tracking-[0.3em]">{item.severity}</span>
            </div>
            <p className="text-xs font-semibold mt-2 text-slate-700 break-words">{item.detail}</p>
            {item.suggestion && (
              <p className="text-[9px] font-black uppercase tracking-[0.2em] mt-2 text-slate-500 break-words">{item.suggestion}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (mode === 'TOAST') {
    return (
      <div className="fixed top-24 right-8 w-[360px] bg-white border border-slate-100 rounded-[2rem] shadow-xl p-6 z-50">
        {content}
      </div>
    );
  }

  if (mode === 'PANEL') {
    return (
      <aside className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8">
        {content}
      </aside>
    );
  }

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8">
      {content}
    </div>
  );
};

export default AgentAdvisories;
